import type {
  GetProductsRequestResponse,
  MultiPriceStoreProductData,
  ProductData,
} from '@innogy/feh/models';
import type {
  GenericProductContent,
  ButtonActions,
  ProductUsp,
  ProductInfo,
  BillingInterval,
} from '@legacy/common-ui-models';
import type {
  ImageFieldValue,
  LinkFieldValue,
  RichTextField,
} from '@sitecore-jss/sitecore-jss-angular';

export function transformToGetProductsRequestResponse(
  data: ProductData[]
): GetProductsRequestResponse {
  const products = data.map((product): MultiPriceStoreProductData => {
    const content: GenericProductContent = mapGenericContent(
      product?.fields?.Content?.fields
    );

    return {
      isSubscription: product.fields?.IsSubscription?.value,
      subscriptionInterval: product.fields?.SubscriptionInterval?.fields?.value
        ?.value as BillingInterval,
      campaignId: product.fields?.CampaignId?.value,
      price: product.fields?.BuyPrice?.value,
      rentPrice: product.fields?.RentPrice?.value,
      suiteCrmId: product.fields?.SuiteCrmId?.value ?? '',
      content,
    };
  });

  return { products };
}

function mapGenericContent(fields: any): GenericProductContent {
  return {
    title: getFieldValue(fields, 'Title'),
    subTitle: getFieldValue(fields, 'SubTitle'),
    longTitle: getOptionalFieldValue(fields, 'LongTitle'),
    longSubtitle: getRichTextField(fields, 'LongSubtitle'),
    image: getImageField(fields),
    text: getFieldValue(fields, 'Text'),
    scores: fields?.Scores,
    usps: getUsps(fields?.Usps),
    productInfo: getProductInfo(fields?.ProductInfo),
    highlight: getFieldValue(fields, 'Highlight'),
    submitButtonText: getFieldValue(fields, 'SubmitButtonText'),
    submitButtonAction: getButtonAction(fields, 'SubmitButtonAction'),
    submitButtonLink: getLinkField(fields, 'SubmitButtonLink'),
    secondaryButtonText: getOptionalFieldValue(fields, 'SecondaryButtonText'),
    secondaryButtonAction: getButtonAction(fields, 'SecondaryButtonAction'),
    secondaryButtonLink: getLinkField(fields, 'SecondaryButtonLink'),
    secondaryButtonStyle: getButtonStyle(fields, 'SecondaryButtonStyle'),
    productPricePrefixLabel: getOptionalFieldValue(
      fields,
      'ProductPricePrefixLabel'
    ),
    productPriceSuffixLabel: getOptionalFieldValue(
      fields,
      'ProductPriceSuffixLabel'
    ),
  };
}

function getFieldValue(fields: any, fieldName: string): string {
  return fields?.[fieldName]?.value;
}

function getOptionalFieldValue(fields: any, fieldName: string): string {
  return fields?.[fieldName]?.value;
}

function getImageField(fields: any): ImageFieldValue | Record<string, never> {
  return fields?.Image?.value;
}

function getRichTextField(fields: any, fieldName: string): RichTextField {
  return fields?.[fieldName]?.value;
}

function getLinkField(fields: any, fieldName: string): LinkFieldValue {
  return fields?.[fieldName]?.value;
}

function getButtonAction(fields: any, fieldName: string): ButtonActions {
  return fields?.[fieldName]?.fields?.value?.value as ButtonActions;
}

function getButtonStyle(fields: any, fieldName: string): string {
  return fields?.[fieldName]?.fields?.value?.value;
}

function getUsps(usps: any): ProductUsp[] {
  return usps?.map(
    (usp: any): ProductUsp => ({
      text: usp.text ?? '',
      inactive: usp.inactive ?? false,
      tooltip: usp.tooltip ?? '',
    })
  );
}

function getProductInfo(productInfo: any): ProductInfo[] {
  return productInfo?.map(
    (info: any): ProductInfo => ({
      icon: info.fields?.Icon?.fields?.value?.value,
      key: info.fields?.Key?.value,
      value: info.fields?.Value?.value,
    })
  );
}
